/**
 * Overview Dashboard — shared layout for Unit Manager and Director/VP.
 * scope="unit" | "system" switches labels and data context.
 * Design: 2-column grid, section cards, Operational Reliability Bar.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EmailSignalEngine } from '../../utils/emailSignalData';
import { fetchDailyFocus } from '../../utils/api';
import AddToQueueButton from '../common/AddToQueueButton';
import { KPICard, Button } from '../ui';

const FOCUS_ITEMS_UNIT = [
  { rank: 1, name: 'Diabetic A1C Outreach', meta: '15 patients overdue', time: '~45 min' },
  { rank: 2, name: 'Med Adherence Follow-ups', meta: '12 pending authorizations', time: '~90 min' },
  { rank: 3, name: 'Post-Discharge Calls', meta: '8 patients', time: '~60 min' },
];

const FOCUS_ITEMS_SYSTEM = [
  { rank: 1, name: 'Regional Staffing Rebalance', meta: '3 sites affected', time: '~60 min' },
  { rank: 2, name: 'Quality Audit Prep — Oncology', meta: '2 sites', time: '~45 min' },
  { rank: 3, name: 'Prior Auth Aging Review', meta: 'System-wide', time: '~30 min' },
];

const STAFFING_BARS_UNIT = [
  { shift: 'AM Shift', detail: 'All positions filled · 7:00 AM – 3:00 PM', filled: 7, total: 7, status: 'Full', statusClass: 'ok' },
  { shift: 'PM Shift', detail: '2 RN call-outs · 3:00 PM – 11:00 PM', filled: 5, total: 7, status: '-2 RN', statusClass: 'short' },
  { shift: 'Night Shift', detail: '1 float pending confirmation · 11:00 PM – 7:00 AM', filled: 6, total: 7, status: 'Pending', statusClass: 'ok' },
];

const STAFFING_BARS_SYSTEM = [
  { shift: 'Florida Cancer Institute', detail: '92% filled', filled: 92, total: 100, status: 'Full', statusClass: 'ok' },
  { shift: 'Bay Area Clinic', detail: '78% filled', filled: 78, total: 100, status: '-3 RN', statusClass: 'short' },
  { shift: 'Midwest Regional', detail: '95% filled', filled: 95, total: 100, status: 'Full', statusClass: 'ok' },
];

const RELIABILITY_PLACEHOLDER = {
  timeToIntervention: '3.2h',
  resolutionRate: '89%',
  backlogVariability: '-34%',
  reliabilityScore: '6.2',
};

function KPIStrip({ scope }) {
  const isSystem = scope === 'system';
  const kpis = isSystem
    ? [
        { label: 'System Quality Composite', value: '82%', trend: '2% vs last month', trendClass: 'up', border: 'var(--aqua)', domain: 'quality' },
        { label: 'Staffing Coverage', value: '94%', trend: 'System-wide ratio', trendClass: 'neutral', border: 'var(--orange-warn)', domain: 'staffing' },
        { label: 'Patient Satisfaction', value: '4.1/5.0', trend: '0.1 vs last quarter', trendClass: 'up', border: 'var(--green)', domain: 'consumer' },
        { label: 'Open Escalations', value: '12', trend: 'System-wide', trendClass: 'neutral', border: 'var(--royal)', domain: 'ops' },
      ]
    : [
        { label: 'Quality Performance', value: '85%', trend: '3% vs last month', trendClass: 'up', border: 'var(--aqua)', domain: 'quality' },
        { label: 'Staffing Status', value: '12/13', trend: '1 RN short today', trendClass: 'down', border: 'var(--orange-warn)', domain: 'staffing' },
        { label: 'Patient Satisfaction', value: '4.3/5.0', trend: '0.2 vs last quarter', trendClass: 'up', border: 'var(--green)', domain: 'consumer' },
        { label: 'Priority Tasks', value: '8', trend: 'Due this week', trendClass: 'neutral', border: 'var(--royal)', domain: 'practice' },
      ];

  return (
    <div className="grid grid-cols-4 gap-3.5 mb-6 overview-kpi-strip" role="region" aria-label="KPI strip">
      {kpis.map((k, i) => (
        <KPICard
          key={k.label}
          size="md"
          density="default"
          label={k.label}
          value={k.value}
          domain={k.domain}
          accentColor={k.border}
          trend={k.trendClass !== 'neutral' ? k.trend : undefined}
          trendDown={k.trendClass === 'down'}
          subLabel={k.trendClass === 'neutral' ? k.trend : undefined}
          className="animate-fade-up"
          style={{ animationDelay: `${0.04 * (i + 1)}s` }}
        />
      ))}
    </div>
  );
}

function FocusTodayCard({ scope, animationDelay = 0.2 }) {
  const isSystem = scope === 'system';
  const title = isSystem ? 'System Focus Today' : 'Your Focus Today';
  const items = isSystem ? FOCUS_ITEMS_SYSTEM : FOCUS_ITEMS_UNIT;
  const rankColors = ['var(--aqua)', 'var(--royal)', 'var(--slate)'];

  return (
    <div className="section-card overview-section-card animate-fade-up" style={{ background: 'var(--white)', borderRadius: 14, boxShadow: 'var(--shadow-card)', overflow: 'hidden', animationDelay: `${animationDelay}s` }}>
      <div className="section-header" style={{ padding: '18px 22px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: 'var(--text-dark)' }}>{title}</div>
        <Link to={isSystem ? '/director/overview' : '/priority-queue'} className="section-action" style={{ fontSize: 12, fontWeight: 600, color: 'var(--aqua)', textDecoration: 'none' }}>View All →</Link>
      </div>
      <div style={{ padding: '0 22px 12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item) => (
            <div key={item.rank} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: 'var(--surface-warm)', borderRadius: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: rankColors[item.rank - 1], color: 'white', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.rank}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-dark)' }}>{item.name}</div>
                <div className="text-caption text-text-muted" style={{ marginTop: 2 }}>{item.meta}</div>
              </div>
              <div className="text-caption text-text-muted" style={{ fontWeight: 600, background: 'var(--surface)', padding: '4px 9px', borderRadius: 5, whiteSpace: 'nowrap' }}>{item.time}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-text-muted" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 22px', borderTop: '1px solid var(--surface)', fontSize: 12 }}>
        <span>Estimated: 3.25 hours</span>
        <span style={{ fontWeight: 600, color: 'var(--aqua)' }}>18/24 tasks (75%)</span>
      </div>
    </div>
  );
}

function DailyOperationalFocusCard({ scope, animationDelay = 0.2 }) {
  const isSystem = scope === 'system';
  const title = isSystem ? 'System Operational Alerts' : 'Daily Operational Focus';
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchDailyFocus()
      .then((data) => {
        if (!cancelled) {
          const sorted = [...(data || [])].sort((a, b) => {
            const order = { critical: 0, high: 1, medium: 2, low: 3 };
            return (order[a.priority] ?? 4) - (order[b.priority] ?? 4);
          });
          setIssues(sorted.slice(0, 5));
        }
      })
      .catch(() => { if (!cancelled) setIssues([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const alertStyle = (priority) => {
    if (priority === 'critical') return { bg: 'var(--red-bg)', border: 'var(--red-critical)', badge: 'var(--red-critical)', badgeColor: 'white' };
    if (priority === 'high') return { bg: 'var(--yellow-bg)', border: 'var(--yellow)', badge: 'var(--yellow)', badgeColor: 'var(--navy-deep)' };
    return { bg: 'var(--green-bg)', border: 'var(--green)', badge: 'var(--green)', badgeColor: 'white' };
  };

  return (
    <div className="section-card overview-section-card animate-fade-up" style={{ background: 'var(--white)', borderRadius: 14, boxShadow: 'var(--shadow-card)', overflow: 'hidden', animationDelay: `${animationDelay}s` }}>
      <div className="section-header" style={{ padding: '18px 22px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: 'var(--text-dark)' }}>{title}</div>
        <Button type="button" variant="ghost" size="sm" onClick={() => window.location.reload()} className="section-action" style={{ fontSize: 12, fontWeight: 600, color: 'var(--aqua)' }}>Refresh</Button>
      </div>
      <div style={{ padding: '0 22px 22px' }}>
        {loading ? (
          <div className="text-text-muted" style={{ padding: 20, textAlign: 'center' }}>Loading…</div>
        ) : issues.length === 0 ? (
          <div style={{ padding: 20, background: 'var(--green-bg)', borderRadius: 8, borderLeft: '3px solid var(--green)' }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-dark)' }}>All Clear</div>
            <div className="text-text-muted" style={{ fontSize: 12 }}>No issues requiring attention right now.</div>
          </div>
        ) : (
          issues.map((issue) => {
            const style = alertStyle(issue.priority);
            return (
              <div key={issue.id} style={{ padding: 14, borderRadius: 8, marginBottom: 8, background: style.bg, borderLeft: `3px solid ${style.border}`, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, padding: '2px 7px', borderRadius: 3, background: style.badge, color: style.badgeColor }}>{(issue.priority || 'info').toUpperCase()}</span>
                  <span className="text-text-muted" style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 3, background: 'rgba(0,0,0,0.04)' }}>{issue.source || 'General'}</span>
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 3 }}>{issue.title}</div>
                {issue.description && <div className="text-text-muted" style={{ fontSize: 12, lineHeight: 1.45 }}>{issue.description}</div>}
                <div style={{ position: 'absolute', top: 14, right: 14 }}><AddToQueueButton title={issue.title} onSuccess={() => {}} buttonStyle="minimal" /></div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function SignalActivityCard({ scope, animationDelay = 0.3 }) {
  const isSystem = scope === 'system';
  const title = isSystem ? 'Cross-Site Signal Activity' : 'Signal Activity';
  const [signalData, setSignalData] = useState(null);

  useEffect(() => {
    try {
      EmailSignalEngine.ensureMockData();
      const threads = EmailSignalEngine.getThreads();
      const top = [...threads].sort((a, b) => b.riskScore - a.riskScore).slice(0, 3);
      const highPriority = threads.filter(t => t.riskScore >= 61).length;
      const resolvedToday = Math.min(threads.length, Math.floor(threads.length * 0.4));
      setSignalData({ highPriority, agingThreads: threads.filter(t => t.riskScore >= 40 && t.riskScore < 61).length, resolvedToday, top });
    } catch {
      setSignalData({ highPriority: 4, agingThreads: 7, resolvedToday: 12, top: [] });
    }
  }, []);

  const linkPath = isSystem ? '/director/email-signal' : '/email-signal';

  return (
    <div className="section-card overview-section-card animate-fade-up" style={{ background: 'var(--white)', borderRadius: 14, boxShadow: 'var(--shadow-card)', overflow: 'hidden', animationDelay: `${animationDelay}s` }}>
      <div className="section-header" style={{ padding: '18px 22px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: 'var(--text-dark)' }}>{title}</div>
        <Link to={linkPath} className="section-action" style={{ fontSize: 12, fontWeight: 600, color: 'var(--aqua)', textDecoration: 'none' }}>Email Signals →</Link>
      </div>
      <div style={{ padding: '0 22px 22px' }}>
        {signalData && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
              <div style={{ background: 'var(--surface-warm)', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 26, fontWeight: 700, color: 'var(--red-critical)', letterSpacing: '-0.3px' }}>{signalData.highPriority}</div>
                <div className="text-caption text-text-muted" style={{ fontWeight: 600 }}>High Priority</div>
              </div>
              <div style={{ background: 'var(--surface-warm)', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 26, fontWeight: 700, color: 'var(--orange-warn)', letterSpacing: '-0.3px' }}>{signalData.agingThreads}</div>
                <div className="text-caption text-text-muted" style={{ fontWeight: 600 }}>Aging Threads</div>
              </div>
              <div style={{ background: 'var(--surface-warm)', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 26, fontWeight: 700, color: 'var(--green)', letterSpacing: '-0.3px' }}>{signalData.resolvedToday}</div>
                <div className="text-caption text-text-muted" style={{ fontWeight: 600 }}>Resolved Today</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {signalData.top.map((t) => {
                const dotColor = t.riskScore >= 61 ? 'var(--red-critical)' : t.riskScore >= 40 ? 'var(--yellow)' : 'var(--green)';
                const ageColor = t.riskScore >= 61 ? 'var(--red-critical)' : t.riskScore >= 40 ? 'var(--orange-warn)' : 'var(--green)';
                const ageLabel = t.riskScore >= 61 ? '72h' : t.riskScore >= 40 ? '36h' : '4h';
                return (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: 'var(--surface-warm)', borderRadius: 7, gap: 10 }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)' }}>{t.subjectMasked}</div>
                      <div className="text-caption text-text-muted" style={{ marginTop: 1 }}>{t.category} · {t.uniqueActors} actors · {t.replyCycles} replies</div>
                    </div>
                    <span className="text-caption" style={{ fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: ageColor === 'var(--red-critical)' ? 'var(--red-bg)' : ageColor === 'var(--orange-warn)' ? 'var(--yellow-bg)' : 'var(--green-bg)', color: ageColor }}>{ageLabel}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {!signalData && <div className="text-text-muted" style={{ padding: 20 }}>Loading…</div>}
      </div>
    </div>
  );
}

function StaffingOverviewCard({ scope, animationDelay = 0.35 }) {
  const isSystem = scope === 'system';
  const title = isSystem ? 'Staffing Overview — All Sites' : 'Staffing Overview';
  const bars = isSystem ? STAFFING_BARS_SYSTEM : STAFFING_BARS_UNIT;

  const ring = (filled, total) => {
    const pct = total ? (filled / total) * 100 : 0;
    const r = 21;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    return { circ, offset, isWarn: pct < 85 && pct >= 70, isShort: pct < 70 };
  };

  return (
    <div className="section-card overview-section-card animate-fade-up" style={{ background: 'var(--white)', borderRadius: 14, boxShadow: 'var(--shadow-card)', overflow: 'hidden', animationDelay: `${animationDelay}s` }}>
      <div className="section-header" style={{ padding: '18px 22px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title" style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: 'var(--text-dark)' }}>{title}</div>
        <Link to={isSystem ? '/director/staffing' : '/staffing'} className="section-action" style={{ fontSize: 12, fontWeight: 600, color: 'var(--aqua)', textDecoration: 'none' }}>Details →</Link>
      </div>
      <div style={{ padding: '0 22px 22px' }}>
        {bars.map((bar) => {
          const { circ, offset, isWarn, isShort } = ring(bar.filled, bar.total);
          const strokeColor = isShort ? 'var(--red-critical)' : isWarn ? 'var(--orange-warn)' : 'var(--green)';
          const displayVal = bar.total === 100 ? `${bar.filled}%` : `${bar.filled}/${bar.total}`;
          return (
            <div key={bar.shift} style={{ display: 'flex', alignItems: 'center', padding: 14, background: 'var(--surface-warm)', borderRadius: 8, gap: 16, marginBottom: 10 }}>
              <div style={{ width: 52, height: 52, position: 'relative', flexShrink: 0 }}>
                <svg width={52} height={52} viewBox="0 0 52 52" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx={26} cy={26} r={21} fill="none" stroke="#E2E8F0" strokeWidth={5} />
                  <circle cx={26} cy={26} r={21} fill="none" stroke={strokeColor} strokeWidth={5} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} />
                </svg>
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--text-dark)', letterSpacing: '-0.3px' }}>{displayVal}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>{bar.shift}</div>
                <div className="text-caption text-text-muted">{bar.detail}</div>
              </div>
              <span className="text-caption" style={{ fontWeight: 600, padding: '3px 9px', borderRadius: 5, background: bar.statusClass === 'short' ? 'var(--red-bg)' : 'var(--green-bg)', color: bar.statusClass === 'short' ? 'var(--red-critical)' : 'var(--green)' }}>{bar.status}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OperationalReliabilityBar({ animationDelay = 0.45 }) {
  const stats = [
    { value: RELIABILITY_PLACEHOLDER.timeToIntervention, label: 'Avg. Time to Intervention' },
    { value: RELIABILITY_PLACEHOLDER.resolutionRate, label: 'Thread Resolution Rate' },
    { value: RELIABILITY_PLACEHOLDER.backlogVariability, label: 'Backlog Variability (7d)' },
    { value: RELIABILITY_PLACEHOLDER.reliabilityScore, label: 'Operational Reliability Score' },
  ];

  return (
    <div
      className="bottom-banner overview-reliability-bar animate-fade-up"
      style={{
        marginTop: 6,
        background: 'var(--navy)',
        borderRadius: 14,
        padding: '18px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        animationDelay: `${animationDelay}s`,
      }}
    >
      {stats.map((s, i) => (
        <React.Fragment key={s.label}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--aqua-bright)', letterSpacing: '-0.3px' }}>{s.value}</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 2 }}>{s.label}</div>
          </div>
          {i < stats.length - 1 && <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.08)' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function OverviewDashboard({ scope = 'unit' }) {
  return (
    <div className="overview-dashboard">
      <KPIStrip scope={scope} />
      <div className="content-grid overview-content-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 20 }}>
        <FocusTodayCard scope={scope} />
        <DailyOperationalFocusCard scope={scope} />
      </div>
      <div className="content-grid overview-content-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 20 }}>
        <SignalActivityCard scope={scope} />
        <StaffingOverviewCard scope={scope} />
      </div>
      <OperationalReliabilityBar />
    </div>
  );
}
